'use client'

import { ArrowRight } from '@/components/svg/arrow-right/arrow-right'
import { ExternalLinkIcon } from '@/components/svg/external-link-icon/external-link-icon'
import { VideoIcon } from '@/components/svg/video-icon/video-icon'
import { XIconNew } from '@/components/svg/x-icon-new/x-icon-new'
import { Image } from '@/components/v2/atoms/image/image'
import { ImageSimple } from '@/utils/articleWidgetsBackwardCompatibility'
import cx from 'classnames'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import ReactPlayer from 'react-player'
import { SlideImage } from 'yet-another-react-lightbox'
import s from './image-lightbox.module.css'

const LightboxV3 = dynamic(
  () => import('@/components/v3/atoms/lightbox/lightbox')
)

type Props = {
  images: ImageSimple[]
  indexDisplay: number
  setIndexDisplay: React.Dispatch<React.SetStateAction<number>>
}

export const ImageLightboxV3 = ({
  images,
  indexDisplay,
  setIndexDisplay,
}: Props) => {
  return (
    <LightboxV3
      open={indexDisplay !== -1}
      index={indexDisplay}
      close={() => setIndexDisplay(-1)}
      slides={images.map((image) => {
        return {
          src: image.url,
          alt: image.alt,
          description: image.description,
          width: 1000,
          height: 1000,
        }
      })}
      render={{
        slide: ({ slide }) =>
          CustomSlide({
            slide,
            images,
            setIndexDisplay,
          }),
        iconClose: () => (
          <span key="close-button" className={s.lightboxButtonClose}>
            <XIconNew iconColor="#fff" />
            Close
          </span>
        ),
        iconNext: () => null,
        iconPrev: () => null,
      }}
    />
  )
}

const CustomSlide = ({
  slide,
  images,
  setIndexDisplay,
}: { slide: SlideImage & { description?: string } } & Omit<
  Props,
  'indexDisplay'
>) => {
  const [isLoading, setIsLoading] = useState(true)
  const [videoActive, setVideoActive] = useState(false)

  const description = slide.description || ''
  const showDescription = !!slide.description
  const showCredits = !!images.find(
    (image) => image.url === slide.src && image.credits
  )
  const byline = images.find((image) => image.url === slide.src)?.photographer
  const bylineIsExternal = !!byline && /^https?:\/\//.test(byline.href)
  const indexDisplay = images.findIndex((image) => image.url === slide.src)
  const videoUrl = images[indexDisplay]?.videoUrl

  const handlePlayVideo = () => {
    if (videoUrl) {
      setVideoActive(true)
    }
  }

  const handleCloseVideo = () => {
    setVideoActive(false)
  }

  const handleNext = () => {
    setIndexDisplay((prev) => (prev + 1) % images.length)
    handleCloseVideo()
  }

  const handlePrev = () => {
    setIndexDisplay((prev) => (prev - 1 + images.length) % images.length)
    handleCloseVideo()
  }

  return (
    <div className={s.lightboxContainer} key={slide.src}>
      {isLoading && <div className={s.loading} />}

      <button
        type="button"
        className={s.lightboxImageButton}
        disabled={isLoading && !videoUrl}
        onClick={handlePlayVideo}
      >
        {!videoActive ? (
          <>
            <Image
              src={slide.src}
              alt={slide.alt || ''}
              width={slide.width!}
              height={slide.height!}
              className={s.lightboxImage}
              onLoad={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
              fit
            />
            {videoUrl && !isLoading && (
              <VideoIcon
                svgProps={{
                  className: s.videoIcon,
                }}
              />
            )}
          </>
        ) : (
          <ReactPlayer url={videoUrl} playing controls />
        )}
      </button>

      <div className={s.lightboxControls}>
        <div className={s.lightboxDescription}>
          <p>
            {showDescription && (
              <>
                <span
                  dangerouslySetInnerHTML={{
                    __html: description?.toString()!,
                  }}
                />
                &nbsp;
              </>
            )}
            ({indexDisplay + 1}/{images.length})
          </p>

          {byline?.name ? (
            <p className={s.credits}>
              <a
                href={byline.href}
                className={s.creditsLink}
                {...(bylineIsExternal
                  ? { target: '_blank', rel: 'noreferrer noopener' }
                  : {})}
              >
                Credit: {byline.name}
                {bylineIsExternal && (
                  <ExternalLinkIcon iconColor="currentColor" />
                )}
              </a>
            </p>
          ) : (
            showCredits && (
              <p className={s.credits}>
                Credit: {images[indexDisplay].credits}
              </p>
            )
          )}
        </div>

        <div className={s.lightboxNavigation}>
          <button
            type="button"
            className={cx(s.lightboxArrow, s.lightboxArrowPrev)}
            onClick={handlePrev}
            aria-label="Previous image"
          >
            <ArrowRight
              svgProps={{
                className: s.arrowPrevIcon,
              }}
            />
          </button>

          <button
            type="button"
            className={cx(s.lightboxArrow, s.lightboxArrowNext)}
            onClick={handleNext}
            aria-label="Next image"
          >
            <ArrowRight />
          </button>
        </div>
      </div>
    </div>
  )
}
